import React, { useState } from "react"
import { Pressable, StyleSheet, Text, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"
import { pressOnSpace } from "../utils/pressOnSpace"
import HourBar, { HOUR_COLORS, TRAINING_TINTS } from "./HourBar"
import InscriptionSheet from "./InscriptionSheet"
import MomentTiles from "./MomentTiles"

const DARK = theme.colors.primaryDark || theme.colors.primary

/** La pastille de l'alternance, à cheval sur les deux dessins. */
const SWAP_SIZE = 32
const SWAP_OVERLAP = 4
/** Du dessin à la jointure des deux temps : la pastille est centrée dessus. */
const SEAM = SWAP_SIZE / 2 - SWAP_OVERLAP
/** La marge intérieure du fond de l'entraînement. */
const PANEL = 10

/**
 * « Une heure de {prénom} » — le volet ouvert par le « i » d'un format : un
 * onglet par format, la barre de son heure, puis les deux temps de l'heure
 * (avec le masteur, en entraînement).
 *
 * `formats` : ce que rend `formatSessions`, dans l'ordre des onglets.
 * `initialKey` est l'onglet ouvert. `copy` porte les textes communs
 * (`title`, `withMasteur`, `training`, `close`).
 */
const FormatInfoSheet = ({ visible, onClose, copy, ...content }) => (
    <InscriptionSheet
        isVisible={visible}
        onClose={onClose}
        title={copy.title}
        closeLabel={copy.close}
    >
        <FormatTabs copy={copy} {...content} />
    </InscriptionSheet>
)

const FormatTabs = ({ formats = [], initialKey, copy }) => {
    const [tab, setTab] = useState(initialKey ?? formats[0]?.key)
    const format = formats.find((f) => f.key === tab) ?? formats[0]

    if (!format) return null

    return (
        <>
            <FormatSwitcher
                formats={formats}
                selectedKey={format.key}
                onSelect={setTab}
            />
            <HourBar visits={format.visits} alt={format.alt} />
            <FormatSession format={format} copy={copy} steady />
        </>
    )
}

/** Les onglets du volet : les formats côte à côte, un seul ouvert. */
const FormatSwitcher = ({ formats = [], selectedKey, onSelect }) => (
    <View role="tablist" style={styles.tabs}>
        {formats.map((f) => {
            const selected = f.key === selectedKey
            const press = () => onSelect?.(f.key)
            return (
                <Pressable
                    key={f.key}
                    role="tab"
                    aria-selected={selected}
                    onPress={press}
                    {...pressOnSpace(press)}
                    style={[styles.tab, selected && styles.tabOn]}
                >
                    <Text
                        style={[styles.tabText, selected && styles.tabTextOn]}
                    >
                        {f.name}
                    </Text>
                </Pressable>
            )
        })}
    </View>
)

/**
 * Ce qu'un format donne à vivre, du point de vue de l'élève : les deux
 * temps de son heure — un dessin et ses mots chacun, l'entraînement sur
 * un fond à sa couleur, et sur le bord de ce fond la pastille qui dit qu'ils
 * alternent —, puis ce qu'il faut en savoir. La barre de l'heure est
 * au-dessus, là où le format se nomme : sous les onglets du volet, sous le
 * titre de la page du format. Les pastilles des deux titres en sont la
 * légende : elles ont ses couleurs.
 *
 * `steady` réserve la place des phrases les plus longues : dans le volet, le
 * bloc garde sa hauteur d'un onglet à l'autre et les onglets restent sous le
 * doigt.
 */
export const FormatSession = ({ format, copy, steady = false }) => (
    <View style={styles.session}>
        <View style={styles.moments}>
            <View style={[styles.moment, styles.withMasteur]}>
                <View>
                    <MomentTiles mode="dialogue" />
                    <View aria-hidden style={styles.swap}>
                        <FontAwesome5
                            name="exchange-alt"
                            size={13}
                            color={theme.colors.white}
                        />
                    </View>
                </View>
                <MomentWords
                    color={HOUR_COLORS.withMasteur}
                    title={copy.withMasteur.title}
                    text={copy.withMasteur.text}
                    steady={steady}
                />
            </View>
            <View
                style={[
                    styles.moment,
                    styles.training,
                    {
                        backgroundColor: format.beside
                            ? TRAINING_TINTS.beside
                            : TRAINING_TINTS.training,
                    },
                ]}
            >
                <MomentTiles mode="training" beside={format.beside} />
                <MomentWords
                    color={
                        format.beside
                            ? HOUR_COLORS.withMasteur
                            : HOUR_COLORS.training
                    }
                    title={copy.training}
                    text={format.training}
                    steady={steady}
                />
            </View>
        </View>
        {format.hour || steady ? (
            <Text style={[styles.hourText, steady && styles.hourSteady]}>
                {format.hour?.text}
                {format.hour?.note ? (
                    <Text style={styles.hourNote}> {format.hour.note}</Text>
                ) : null}
            </Text>
        ) : null}
    </View>
)

const MomentWords = ({ color, title, text, steady }) => (
    <View style={styles.words}>
        <View style={styles.wordsHead}>
            <View style={[styles.chip, { backgroundColor: color }]} />
            <Text style={styles.momentTitle}>{title}</Text>
        </View>
        <Text style={[styles.momentText, steady && styles.momentSteady]}>
            {text}
        </Text>
    </View>
)

const styles = StyleSheet.create({
    tabs: {
        flexDirection: "row",
        gap: 4,
        padding: 4,
        borderRadius: 14,
        backgroundColor: theme.colors.lighter,
    },
    tab: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: 8,
        paddingHorizontal: 4,
        borderRadius: 11,
        borderWidth: 1.5,
        borderColor: "transparent",
    },
    tabOn: {
        backgroundColor: theme.colors.white,
        borderColor: theme.colors.primary,
    },
    tabText: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12.5,
        color: theme.colors.grey,
    },
    tabTextOn: { color: DARK },
    session: { gap: 14 },
    moments: { flexDirection: "row" },
    // Un temps : son dessin, puis ses mots. Chacun part de ses propres marges
    // (`flexBasis`) : les deux dessins ont la même largeur.
    moment: {
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
        gap: 8,
        paddingVertical: PANEL,
    },
    // Au-dessus de son voisin : la pastille déborde sur le fond de l'autre.
    withMasteur: { zIndex: 1, flexBasis: SEAM, paddingRight: SEAM },
    training: {
        flexBasis: SEAM + PANEL,
        paddingLeft: SEAM,
        paddingRight: PANEL,
        borderRadius: 14,
    },
    // Centrée sur le bord du fond : on lit d'abord que les deux temps alternent.
    swap: {
        position: "absolute",
        right: -(SEAM + SWAP_SIZE / 2),
        top: "50%",
        marginTop: -SWAP_SIZE / 2,
        width: SWAP_SIZE,
        height: SWAP_SIZE,
        borderRadius: SWAP_SIZE / 2,
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 2,
        borderColor: theme.colors.white,
        backgroundColor: theme.colors.primary,
    },
    words: { gap: 2 },
    wordsHead: { flexDirection: "row", alignItems: "center", gap: 6 },
    chip: { width: 9, height: 9, borderRadius: 3 },
    momentTitle: {
        flexShrink: 1,
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 12.5,
        lineHeight: 16,
        color: DARK,
    },
    momentText: {
        fontFamily: "Montserrat-Medium",
        fontSize: 11.5,
        lineHeight: 15,
        color: theme.colors.grey,
    },
    // Trois lignes réservées, dans le volet.
    momentSteady: { minHeight: 45 },
    hourText: {
        fontFamily: "Montserrat-Medium",
        fontSize: 12.5,
        lineHeight: 17,
        color: theme.colors.grey,
    },
    hourSteady: { minHeight: 34 },
    hourNote: { fontFamily: "Montserrat-Bold", color: DARK },
})

export default FormatInfoSheet
